import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ViewChild,
  ElementRef, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';

import { isNullOrUndefined } from '@app/util/util';
import { OptionSelected } from '@booking/entities/booking-arrangement';
import { BillingCity } from '@billing/entities/billing.payload';
import { PERSON_FORM_INPUTS, PERSON_ADDRESS_FORM_INPUTS } from '@booking/constants/forms.constants';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { removeSubscriptions } from '@shared/helpers/shared-helpers';
import { FORM_COMPONENTS_PROPS } from '@forms/form-components/constants/form-components-props.constants';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyColor, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-form-city-selector',
  templateUrl: './form-city-selector.component.html',
  styleUrls: ['./form-city-selector.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormCitySelectorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() isSubmitted: boolean;
  @Input() isBilling: boolean = true;
  @Input() selector: string = PERSON_ADDRESS_FORM_INPUTS.CITY_CODE;
  @Input() cities: BillingCity[] = [];
  @Input() defaultCity: string;
  @Input() isMobile: boolean;

  @Output() selected: EventEmitter<string> = new EventEmitter();

  @ViewChild('city') public City: ElementRef;

  private subscriptions: Subscription[] = [];
  private setArraySubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private setDefaultSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public readonly iconLocation = Icon.Location;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly formInputsService: FormInputsService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.[FORM_COMPONENTS_PROPS.CITIES]) {
      this.setArraySubject$.next(true);
    }
    if (changes?.[FORM_COMPONENTS_PROPS.DEFAULT_CITY]) {
      this.setDefaultSubject$.next(true);
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.setArraySubject$.pipe(
        distinctUntilChanged(),
        tap(setArray => {
          if (setArray) {
            this.setValues(true);
            this.setArraySubject$.next(false);
          }
        })
      ).subscribe()
    );
    this.subscriptions.push(
      this.setDefaultSubject$.pipe(
        distinctUntilChanged(),
        tap(setDefault => {
          if (setDefault) {
            this.setValues(false);
            this.setDefaultSubject$.next(false);
          }
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    removeSubscriptions(this.subscriptions);
  }

  get addressForm(): UntypedFormGroup {
    return this.form?.get(PERSON_FORM_INPUTS.ADDRESS) as UntypedFormGroup;
  }

  get defaultCityIndex(): number {
    return this.cities.findIndex(c => c.code === this.defaultCity);
  }

  get defaultCityName(): string {
    return this.defaultCityIndex > -1 ? this.cities[this.defaultCityIndex].description : '';
  }

  public onSelected(city: OptionSelected): void {
    const { value } = city;
    const fullAdress = this.cities.find(cityData => cityData.description === value.toString() ||
      cityData.code === value.toString());
    if (!isNullOrUndefined(fullAdress)) {
      this.selected.emit(fullAdress.code);
    } else {
      this.selected.emit(value.toString());
    }
  }

  public citiesName(): string[] {
    return this.cities.map(city => city.description);
  }

  public isFieldInvalid(field: string): boolean {
    return this.formInputsService.isFieldInvalid(field, this.addressForm, this.isSubmitted);
  }

  private setValues(includeIndex: boolean = false): void {
    if (includeIndex && this.defaultCityIndex >= 0 && this.defaultCity) {
      this.onSelected({ value: this.defaultCity, index: this.defaultCityIndex });
    } else if (!includeIndex && this.defaultCity) {
      this.onSelected({ value: this.defaultCity, index: null });
    }
  }

}
